import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, Circle } from 'lucide-react'
import { useSignupFlow } from '../hooks/useSignupFlow'
import { RithmoWordmark } from './shell/OrgMark'
import { productBrand as brand } from './shell/brand'

/**
 * The customer build's signup page, in the live Control Board's sign-in layout (CustomerLoginPage), on Rithmo's own
 * signup flow (useSignupFlow: the same fields, rules, call and next stop as pages/SignupPage.jsx). A new account becomes
 * the admin of a new, empty workspace.
 */
const field = 'mt-2 w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-400'

export default function CustomerSignupPage() {
  const { submit, loading, error, passwordChecks } = useSignupFlow()
  const [f, setF] = useState({ name: '', email: '', companyName: '', jobTitle: '', signupRoleBand: '', password: '' })
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })
  const checks = passwordChecks(f.password)
  const ready = checks.every((c) => c.check) && f.name.trim() && f.email.trim() && f.companyName.trim() && f.jobTitle.trim() && f.signupRoleBand

  return (
    <div className="grid min-h-screen bg-gray-50 lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-brand-gradient p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <RithmoWordmark size={36} onDark />
        </div>
        <div className="max-w-md">
          <h2 className="text-3xl font-bold leading-tight">{brand.signInHeadline}</h2>
          <p className="mt-4 text-base leading-relaxed text-white/75">
            Create your organization’s Control Board. It starts empty: you add your org chart, your agents and the tools your team works in,
            and everything you see comes from what you and your agents actually do.
          </p>
        </div>
        <p className="text-sm text-white/60">{brand.governedBy}</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={(e) => { e.preventDefault(); void submit(f) }} className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <RithmoWordmark size={36} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Create your account</h1>
          <p className="mt-2 text-sm text-gray-500">You will be the admin of your organization’s new workspace.</p>

          {error && <div role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

          <label htmlFor="signup-name" className="mt-6 block text-sm font-semibold text-gray-900">Full name</label>
          <input id="signup-name" autoComplete="name" required placeholder="Full name" value={f.name} onChange={set('name')} className={field} />
          <label htmlFor="signup-email" className="mt-4 block text-sm font-semibold text-gray-900">Work email</label>
          <input id="signup-email" type="email" autoComplete="email" required placeholder="Work email" value={f.email} onChange={set('email')} className={field} />
          <label htmlFor="signup-company" className="mt-4 block text-sm font-semibold text-gray-900">Company</label>
          <input id="signup-company" autoComplete="organization" required placeholder="Company name" value={f.companyName} onChange={set('companyName')} className={field} />
          <label htmlFor="signup-title" className="mt-4 block text-sm font-semibold text-gray-900">Job title</label>
          <input id="signup-title" autoComplete="organization-title" required placeholder="Job title" value={f.jobTitle} onChange={set('jobTitle')} className={field} />
          <label htmlFor="signup-role-band" className="mt-4 block text-sm font-semibold text-gray-900">Which best describes your role at your company?</label>
          <select id="signup-role-band" required value={f.signupRoleBand} onChange={set('signupRoleBand')} className={field}>
            <option value="">Select one</option>
            <option value="senior_leadership">Senior leadership</option>
            <option value="middle_management">Manager / team lead</option>
            <option value="individual_other">Individual contributor / exploring</option>
          </select>
          <label htmlFor="signup-password" className="mt-4 block text-sm font-semibold text-gray-900">Password</label>
          <input id="signup-password" type="password" autoComplete="new-password" required placeholder="Create password" value={f.password} onChange={set('password')} className={field} />
          <ul className="mt-2 space-y-1 text-xs">
            {checks.map((c) => (
              <li key={c.label} className={`flex items-center gap-1.5 ${c.check ? 'text-emerald-700' : 'text-gray-600'}`}>
                {c.check ? <CheckCircle2 size={13} /> : <Circle size={13} />} {c.label}
              </li>
            ))}
          </ul>

          <button type="submit" disabled={loading || !ready}
            className="mt-6 w-full rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60">
            {loading ? 'Creating your account…' : 'Create account'}
          </button>
          <p className="mt-4 text-center text-xs text-gray-600">
            By creating an account you agree to the <Link to="/terms" className="font-semibold text-primary-700">Terms of Service</Link> and <Link to="/privacy" className="font-semibold text-primary-700">Privacy Policy</Link>.
          </p>
          <p className="mt-6 text-center text-sm text-gray-500">
            Already have an account? <Link to="/login" className="font-semibold text-primary-700 hover:text-primary-800">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  )
}
